import React, { useEffect, useMemo, useState } from "react";
import {
    CAlert,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CDropdown,
    CDropdownMenu,
    CDropdownToggle,
    CForm,
    CFormCheck,
    CFormSelect,
    CSpinner,
    CTable,
    CTableBody,
    CTableDataCell,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
} from "@coreui/react";

import { fetchReports } from "../api/reports";
import { createMontlyReport, fetchMontlyReports } from "../api/montlyReports";
import { t } from "../i18n";

const EXPENSE_COLORS = [
    "#2563eb",
    "#dc2626",
    "#16a34a",
    "#9333ea",
    "#ea580c",
    "#0891b2",
    "#be123c",
    "#4f46e5",
    "#65a30d",
    "#c026d3",
];

export default function DashboardPage() {
    const currentYear = new Date().getFullYear();

    const [reports, setReports] = useState([]);
    const [montlyReports, setMontlyReports] = useState([]);
    const [selectedReportIds, setSelectedReportIds] = useState([]);
    const [chartKind, setChartKind] = useState("expenses");
    const [chartType, setChartType] = useState("line");
    const [visibleExpenseIds, setVisibleExpenseIds] = useState([]);
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const comparison = useMemo(
        () => buildExpenseComparison(montlyReports, selectedYear),
        [montlyReports, selectedYear]
    );
    const comparisonYears = useMemo(
        () => buildComparisonYears(montlyReports, currentYear),
        [montlyReports, currentYear]
    );
    const chartSourceRows = chartKind === "income" ? comparison.incomes : comparison.expenses;
    const chartRows = useMemo(
        () => chartSourceRows.map((expense, index) => ({
            ...expense,
            color: EXPENSE_COLORS[index % EXPENSE_COLORS.length],
        })),
        [chartSourceRows]
    );
    const visibleChartRows = useMemo(
        () => chartRows.filter((expense) => visibleExpenseIds.includes(expense.id)),
        [chartRows, visibleExpenseIds]
    );

    useEffect(() => {
        let isMounted = true;

        async function loadDashboard() {
            try {
                const [loadedReports, loadedMontlyReports] = await Promise.all([
                    fetchReports(),
                    fetchMontlyReports(),
                ]);

                if (isMounted) {
                    setReports(loadedReports);
                    setMontlyReports(loadedMontlyReports);
                    setError(null);
                }
            } catch (loadError) {
                if (isMounted) {
                    setError(loadError.message);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        }

        loadDashboard();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (chartKind === "expenses" && comparison.expenses.length === 0 && comparison.incomes.length > 0) {
            setChartKind("income");
        } else if (chartKind === "income" && comparison.incomes.length === 0 && comparison.expenses.length > 0) {
            setChartKind("expenses");
        }
    }, [chartKind, comparison.expenses.length, comparison.incomes.length]);

    useEffect(() => {
        const expenseIds = chartRows.map((expense) => expense.id);

        setVisibleExpenseIds((currentExpenseIds) => {
            const availableExpenseIds = new Set(expenseIds);
            const nextExpenseIds = currentExpenseIds.filter((expenseId) =>
                availableExpenseIds.has(expenseId)
            );

            expenseIds.forEach((expenseId) => {
                if (!currentExpenseIds.includes(expenseId)) {
                    nextExpenseIds.push(expenseId);
                }
            });

            if (
                nextExpenseIds.length === currentExpenseIds.length &&
                nextExpenseIds.every((expenseId, index) => expenseId === currentExpenseIds[index])
            ) {
                return currentExpenseIds;
            }

            return nextExpenseIds;
        });
    }, [chartRows]);

    function toggleReportSelection(reportId) {
        setSelectedReportIds((currentReportIds) => {
            if (currentReportIds.includes(reportId)) {
                return currentReportIds.filter((currentReportId) => currentReportId !== reportId);
            }

            return [...currentReportIds, reportId];
        });
    }

    function toggleExpenseVisibility(expenseId, event) {
        if (event?.metaKey) {
            setVisibleExpenseIds((currentExpenseIds) => {
                if (currentExpenseIds.includes(expenseId)) {
                    return currentExpenseIds.filter((currentExpenseId) => currentExpenseId !== expenseId);
                }

                return [...currentExpenseIds, expenseId];
            });
            return;
        }

        setVisibleExpenseIds((currentExpenseIds) => {
            const onlyThisExpenseSelected =
                currentExpenseIds.length === 1 && currentExpenseIds[0] === expenseId;

            if (onlyThisExpenseSelected) {
                return chartRows.map((expense) => expense.id);
            }

            return [expenseId];
        });
    }

    function selectAllExpenses() {
        setVisibleExpenseIds(chartRows.map((expense) => expense.id));
    }

    function clearSelectedExpenses() {
        setVisibleExpenseIds([]);
    }

    async function handleSubmit(event) {
        event.preventDefault();

        setError(null);
        setSubmitting(true);

        try {
            const updatedMontlyReports = await createMontlyReport({
                reportIds: selectedReportIds,
            });

            setMontlyReports(updatedMontlyReports);
            setSelectedReportIds([]);
        } catch (createError) {
            setError(createError.message);
        } finally {
            setSubmitting(false);
        }
    }

    function formatAmount(amount) {
        return new Intl.NumberFormat("lt-LT", {
            style: "currency",
            currency: "EUR",
        }).format(Math.abs(Number(amount)));
    }

    const canSubmit = selectedReportIds.length > 0 && !submitting;
    const reportsDropdownText =
        selectedReportIds.length > 0
            ? t("dashboard.selectedReports", { count: selectedReportIds.length })
            : t("dashboard.chooseReports");

    return (
        <>
            <div className="mb-4">
                <h1>{t("dashboard.title")}</h1>
                <p className="text-body-secondary mb-0">
                    {t("dashboard.description")}
                </p>
            </div>

            <CCard className="mb-4">
                <CCardHeader>
                    <strong>{t("dashboard.create")}</strong>
                </CCardHeader>

                <CCardBody>
                    {error && (
                        <CAlert color="danger">
                            {error}
                        </CAlert>
                    )}

                    <CForm onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center gap-3 mb-2">
                                <label className="form-label mb-0">
                                    {t("dashboard.reportsLabel")}
                                </label>
                            </div>

                            <CDropdown autoClose="outside">
                                <CDropdownToggle
                                    color="secondary"
                                    disabled={loading || submitting || reports.length === 0}
                                    variant="outline"
                                >
                                    {reportsDropdownText}
                                </CDropdownToggle>

                                {reports.length === 0 ? (
                                    <div className="text-body-secondary p-3">
                                        {t("dashboard.noReports")}
                                    </div>
                                ) : (
                                    <CDropdownMenu
                                        className="p-0 overflow-auto"
                                        style={{ maxHeight: "280px", minWidth: "320px" }}
                                    >
                                        {reports.map((report) => {
                                            const reportId = String(report.id);
                                            const checked = selectedReportIds.includes(reportId);

                                            return (
                                                <label
                                                    className="dropdown-item d-flex align-items-center gap-3 mb-0"
                                                    key={report.id}
                                                >
                                                    <CFormCheck
                                                        checked={checked}
                                                        disabled={loading || submitting}
                                                        onChange={() => toggleReportSelection(reportId)}
                                                    />

                                                    <span className="fw-semibold">
                                                        {report.title}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </CDropdownMenu>
                                )}
                            </CDropdown>
                        </div>

                        <CButton color="primary" type="submit" disabled={!canSubmit}>
                            {submitting ? t("dashboard.creating") : t("dashboard.createButton")}
                        </CButton>
                    </CForm>
                </CCardBody>
            </CCard>

            <CCard>
                <CCardHeader>
                    <strong>{t("dashboard.comparison")}</strong>
                </CCardHeader>

                <CCardBody>
                    {loading && (
                        <div className="d-flex align-items-center gap-2 text-body-secondary">
                            <CSpinner size="sm" />
                            {t("dashboard.loading")}
                        </div>
                    )}

                    {!loading && reports.length === 0 && (
                        <p className="text-body-secondary mb-0">
                            {t("dashboard.noReports")}
                        </p>
                    )}

                    {!loading && reports.length > 0 && montlyReports.length === 0 && (
                        <p className="text-body-secondary mb-0">
                            {t("dashboard.empty")}
                        </p>
                    )}

                    {!loading && montlyReports.length > 0 && (
                        <>
                            <div className="d-flex flex-column flex-sm-row align-items-sm-end gap-3 mb-3">
                                <div style={{ maxWidth: "220px", width: "100%" }}>
                                    <CFormSelect
                                        label={t("dashboard.year")}
                                        value={selectedYear}
                                        onChange={(event) => setSelectedYear(Number(event.target.value))}
                                    >
                                        {comparisonYears.map((year) => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </CFormSelect>
                                </div>

                                <div>
                                    <div className="form-label">{t("dashboard.graph")}</div>
                                    <div className="btn-group">
                                        <CButton
                                            color="primary"
                                            disabled={comparison.expenses.length === 0}
                                            onClick={() => setChartKind("expenses")}
                                            type="button"
                                            variant={chartKind === "expenses" ? undefined : "outline"}
                                        >
                                            {t("dashboard.expenses")}
                                        </CButton>
                                        <CButton
                                            color="primary"
                                            disabled={comparison.incomes.length === 0}
                                            onClick={() => setChartKind("income")}
                                            type="button"
                                            variant={chartKind === "income" ? undefined : "outline"}
                                        >
                                            {t("dashboard.income")}
                                        </CButton>
                                    </div>
                                </div>

                                <div>
                                    <div className="form-label">{t("dashboard.chartType")}</div>
                                    <div className="btn-group">
                                        <CButton
                                            color="primary"
                                            onClick={() => setChartType("line")}
                                            type="button"
                                            variant={chartType === "line" ? undefined : "outline"}
                                        >
                                            {t("dashboard.lineChart")}
                                        </CButton>
                                        <CButton
                                            color="primary"
                                            onClick={() => setChartType("pie")}
                                            type="button"
                                            variant={chartType === "pie" ? undefined : "outline"}
                                        >
                                            {t("dashboard.pieChart")}
                                        </CButton>
                                    </div>
                                </div>
                            </div>

                            {comparison.expenses.length === 0 && comparison.incomes.length === 0 ? (
                                <p className="text-body-secondary mb-0">
                                    {t("dashboard.emptyYear")}
                                </p>
                            ) : (
                                <>
                                    {chartRows.length > 0 ? (
                                        <div className="d-flex flex-column flex-lg-row gap-4 mb-4">
                                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                {chartType === "line" ? (
                                                    <ExpenseLineChart
                                                        expenses={visibleChartRows}
                                                        formatAmount={formatAmount}
                                                        months={comparison.months}
                                                    />
                                                ) : (
                                                    <ExpensePieChart
                                                        expenses={visibleChartRows}
                                                        formatAmount={formatAmount}
                                                        months={comparison.months}
                                                    />
                                                )}
                                            </div>

                                            <aside
                                                className="border-start ps-lg-4"
                                                style={{ flex: "0 0 260px" }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                                                    <strong>{t("dashboard.legend")}</strong>

                                                    <div className="btn-group btn-group-sm">
                                                        <CButton
                                                            color="secondary"
                                                            disabled={visibleExpenseIds.length === chartRows.length}
                                                            onClick={selectAllExpenses}
                                                            type="button"
                                                            variant="outline"
                                                        >
                                                            {t("dashboard.selectAll")}
                                                        </CButton>
                                                        <CButton
                                                            color="secondary"
                                                            disabled={visibleExpenseIds.length === 0}
                                                            onClick={clearSelectedExpenses}
                                                            type="button"
                                                            variant="outline"
                                                        >
                                                            {t("dashboard.clear")}
                                                        </CButton>
                                                    </div>
                                                </div>

                                                <div
                                                    className="d-flex flex-column gap-2 overflow-auto pe-1"
                                                    style={{ maxHeight: "300px" }}
                                                >
                                                    {chartRows.map((expense) => (
                                                        <button
                                                            aria-pressed={visibleExpenseIds.includes(expense.id)}
                                                            className={`btn btn-link d-flex align-items-center gap-2 p-0 text-start text-decoration-none ${
                                                                visibleExpenseIds.includes(expense.id)
                                                                    ? "text-body"
                                                                    : "text-body-secondary opacity-50"
                                                            }`}
                                                            key={expense.id}
                                                            onClick={(event) => toggleExpenseVisibility(expense.id, event)}
                                                            type="button"
                                                        >
                                                            <span
                                                                aria-hidden="true"
                                                                className="rounded-circle flex-shrink-0"
                                                                style={{
                                                                    backgroundColor: expense.color,
                                                                    height: "10px",
                                                                    width: "10px",
                                                                }}
                                                            />
                                                            <span className="text-break">{expense.title}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </aside>
                                        </div>
                                    ) : (
                                        <p className="text-body-secondary mb-4">
                                            {t("dashboard.noGraphData")}
                                        </p>
                                    )}

                                    {comparison.expenses.length > 0 ? (
                                        <ComparisonAmountTable
                                            formatAmount={formatAmount}
                                            months={comparison.months}
                                            rows={comparison.expenses}
                                            title={t("dashboard.expenses")}
                                        />
                                    ) : (
                                        <p className={`text-body-secondary ${
                                            comparison.incomes.length > 0 ? "mb-4" : "mb-0"
                                        }`}>
                                            {t("dashboard.noExpensesYear")}
                                        </p>
                                    )}

                                    {comparison.incomes.length > 0 && (
                                        <div className={comparison.expenses.length > 0 ? "mt-4" : ""}>
                                            <ComparisonAmountTable
                                                formatAmount={formatAmount}
                                                months={comparison.months}
                                                rows={comparison.incomes}
                                                title={t("dashboard.income")}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </CCardBody>
            </CCard>
        </>
    );
}

function ComparisonAmountTable({ formatAmount, months, rows, title }) {
    return (
        <>
            <h5 className="mb-3">{title}</h5>

            <CTable responsive hover className="mb-0 align-middle">
                <CTableHead>
                    <CTableRow>
                        <CTableHeaderCell scope="col">
                            {t("dashboard.category")}
                        </CTableHeaderCell>

                        {months.map((month) => (
                            <CTableHeaderCell
                                className="text-end"
                                key={month.key}
                                scope="col"
                            >
                                {month.label}
                            </CTableHeaderCell>
                        ))}

                        <CTableHeaderCell className="text-end" scope="col">
                            {t("dashboard.total")}
                        </CTableHeaderCell>
                    </CTableRow>
                </CTableHead>

                <CTableBody>
                    {rows.map((row) => (
                        <CTableRow key={row.id}>
                            <CTableDataCell className="fw-semibold">
                                {row.title}
                            </CTableDataCell>

                            {months.map((month) => (
                                <CTableDataCell className="text-end" key={month.key}>
                                    {formatAmount(row.amountsByMonth.get(month.key) || 0)}
                                </CTableDataCell>
                            ))}

                            <CTableDataCell className="text-end fw-semibold">
                                {formatAmount(
                                    months.reduce(
                                        (total, month) =>
                                            total + (row.amountsByMonth.get(month.key) || 0),
                                        0
                                    )
                                )}
                            </CTableDataCell>
                        </CTableRow>
                    ))}
                </CTableBody>
            </CTable>
        </>
    );
}

function ExpenseLineChart({ expenses, formatAmount, months }) {
    const width = 760;
    const height = 340;
    const amounts = expenses.flatMap((expense) =>
        months.map((month) => getChartAmount(expense, month))
    );
    const minAmount = amounts.length > 0 ? Math.min(...amounts) : 0;
    const maxAmount = amounts.length > 0 ? Math.max(...amounts) : 1;
    const amountRange = maxAmount - minAmount;
    const yPadding = amountRange > 0 ? amountRange * 0.12 : Math.max(Math.abs(maxAmount) * 0.12, 1);
    const yMin = minAmount - yPadding;
    const yMax = maxAmount + yPadding;
    const yRange = yMax - yMin;
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => yMin + yRange * ratio);
    const yTickLabels = yTicks.map((tick) => formatAmount(tick));
    const leftPadding = Math.max(
        76,
        Math.max(...yTickLabels.map((label) => label.length)) * 8 + 22
    );
    const padding = {
        top: 24,
        right: 28,
        bottom: 48,
        left: leftPadding,
    };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const getX = (index) => {
        if (months.length <= 1) {
            return padding.left + plotWidth / 2;
        }

        return padding.left + (plotWidth * index) / (months.length - 1);
    };
    const getY = (amount) => padding.top + plotHeight - ((amount - yMin) / yRange) * plotHeight;

    function buildLinePath(expense) {
        return months.map((month, index) => {
            const x = getX(index);
            const y = getY(getChartAmount(expense, month));

            return `${index === 0 ? "M" : "L"} ${x} ${y}`;
        }).join(" ");
    }

    return (
        <div className="border rounded p-3">
            <svg
                aria-label={t("dashboard.graph")}
                className="d-block w-100"
                role="img"
                style={{ minHeight: "260px" }}
                viewBox={`0 0 ${width} ${height}`}
            >
                <rect fill="var(--cui-body-bg)" height={height} width={width} />

                {yTicks.map((tick, index) => {
                    const y = getY(tick);

                    return (
                        <g key={tick}>
                            <line
                                stroke="var(--cui-border-color)"
                                strokeWidth="1"
                                x1={padding.left}
                                x2={width - padding.right}
                                y1={y}
                                y2={y}
                            />
                            <text
                                fill="var(--cui-secondary-color)"
                                fontSize="13"
                                textAnchor="end"
                                x={padding.left - 12}
                                y={y + 4}
                            >
                                {yTickLabels[index]}
                            </text>
                        </g>
                    );
                })}

                <line
                    stroke="var(--cui-border-color)"
                    strokeWidth="1.5"
                    x1={padding.left}
                    x2={padding.left}
                    y1={padding.top}
                    y2={height - padding.bottom}
                />
                <line
                    stroke="var(--cui-border-color)"
                    strokeWidth="1.5"
                    x1={padding.left}
                    x2={width - padding.right}
                    y1={height - padding.bottom}
                    y2={height - padding.bottom}
                />

                {months.map((month, index) => (
                    <g key={month.key}>
                        <line
                            stroke="var(--cui-border-color)"
                            strokeWidth="1"
                            x1={getX(index)}
                            x2={getX(index)}
                            y1={height - padding.bottom}
                            y2={height - padding.bottom + 6}
                        />
                        <text
                            fill="var(--cui-secondary-color)"
                            fontSize="13"
                            textAnchor="middle"
                            x={getX(index)}
                            y={height - 18}
                        >
                            {month.label}
                        </text>
                    </g>
                ))}

                {expenses.map((expense) => (
                    <g key={expense.id}>
                        <path
                            d={buildLinePath(expense)}
                            fill="none"
                            stroke={expense.color}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                        />
                        {months.map((month, index) => {
                            const amount = getChartAmount(expense, month);

                            return (
                                <circle
                                    cx={getX(index)}
                                    cy={getY(amount)}
                                    fill="var(--cui-body-bg)"
                                    key={month.key}
                                    r="4"
                                    stroke={expense.color}
                                    strokeWidth="2"
                                >
                                    <title>{`${expense.title} ${month.label}: ${formatAmount(amount)}`}</title>
                                </circle>
                            );
                        })}
                    </g>
                ))}

                {expenses.length === 0 && (
                    <text
                        fill="var(--cui-secondary-color)"
                        fontSize="16"
                        textAnchor="middle"
                        x={width / 2}
                        y={height / 2}
                    >
                        {t("dashboard.noSelectedExpenses")}
                    </text>
                )}
            </svg>
        </div>
    );
}

function ExpensePieChart({ expenses, formatAmount, months }) {
    const [hoveredExpenseId, setHoveredExpenseId] = useState(null);
    const width = 760;
    const height = 340;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = 154;
    const slices = expenses.map((expense) => {
        const amount = months.reduce(
            (sum, month) => sum + getChartAmount(expense, month),
            0
        );

        return {
            ...expense,
            amount,
        };
    }).filter((expense) => expense.amount > 0);
    const totalAmount = slices.reduce((sum, expense) => sum + expense.amount, 0);
    const hoveredExpense = slices.find((expense) => expense.id === hoveredExpenseId);
    let currentAngle = -Math.PI / 2;

    return (
        <div className="border rounded p-3">
            <svg
                aria-label={t("dashboard.pieChart")}
                className="d-block w-100"
                role="img"
                style={{ minHeight: "260px" }}
                viewBox={`0 0 ${width} ${height}`}
            >
                <rect fill="var(--cui-body-bg)" height={height} width={width} />

                {slices.map((expense) => {
                    const startAngle = currentAngle;
                    const angle = (expense.amount / totalAmount) * Math.PI * 2;
                    const endAngle = startAngle + angle;
                    currentAngle = endAngle;
                    const labelAngle = startAngle + angle / 2;
                    const labelRadius = radius * 0.68;
                    const percentage = Math.round((expense.amount / totalAmount) * 100);

                    return (
                        <g key={expense.id}>
                            <path
                                d={buildPieSlicePath({
                                    centerX,
                                    centerY,
                                    endAngle,
                                    radius,
                                    startAngle,
                                })}
                                fill={expense.color}
                                onBlur={() => setHoveredExpenseId(null)}
                                onFocus={() => setHoveredExpenseId(expense.id)}
                                onMouseEnter={() => setHoveredExpenseId(expense.id)}
                                onMouseLeave={() => setHoveredExpenseId(null)}
                                stroke="var(--cui-body-bg)"
                                strokeWidth="2"
                                style={{ cursor: "default" }}
                                tabIndex="0"
                            >
                                <title>{`${expense.title}: ${formatAmount(expense.amount)} (${percentage}%)`}</title>
                            </path>

                            {percentage >= 5 && (
                                <text
                                    fill="#fff"
                                    fontSize="14"
                                    fontWeight="700"
                                    textAnchor="middle"
                                    x={centerX + Math.cos(labelAngle) * labelRadius}
                                    y={centerY + Math.sin(labelAngle) * labelRadius + 5}
                                >
                                    {percentage}%
                                </text>
                            )}
                        </g>
                    );
                })}

                {slices.length > 0 && (
                    <>
                        <circle
                            cx={centerX}
                            cy={centerY}
                            fill="var(--cui-body-bg)"
                            r="68"
                        />
                        <text
                            fill="var(--cui-secondary-color)"
                            fontSize="13"
                            textAnchor="middle"
                            x={centerX}
                            y={centerY - 6}
                        >
                            {hoveredExpense ? hoveredExpense.title : t("dashboard.total")}
                        </text>
                        <text
                            fill="var(--cui-body-color)"
                            fontSize="18"
                            fontWeight="700"
                            textAnchor="middle"
                            x={centerX}
                            y={centerY + 18}
                        >
                            {formatAmount(hoveredExpense ? hoveredExpense.amount : totalAmount)}
                        </text>
                    </>
                )}

                {slices.length === 0 && (
                    <text
                        fill="var(--cui-secondary-color)"
                        fontSize="16"
                        textAnchor="middle"
                        x={centerX}
                        y={centerY}
                    >
                        {t("dashboard.noSelectedExpenses")}
                    </text>
                )}
            </svg>
        </div>
    );
}

function buildPieSlicePath({ centerX, centerY, endAngle, radius, startAngle }) {
    const fullCircle = endAngle - startAngle >= Math.PI * 2 - 0.0001;

    if (fullCircle) {
        return [
            `M ${centerX} ${centerY - radius}`,
            `A ${radius} ${radius} 0 1 1 ${centerX} ${centerY + radius}`,
            `A ${radius} ${radius} 0 1 1 ${centerX} ${centerY - radius}`,
            "Z",
        ].join(" ");
    }

    const startX = centerX + Math.cos(startAngle) * radius;
    const startY = centerY + Math.sin(startAngle) * radius;
    const endX = centerX + Math.cos(endAngle) * radius;
    const endY = centerY + Math.sin(endAngle) * radius;
    const largeArcFlag = endAngle - startAngle > Math.PI ? 1 : 0;

    return [
        `M ${centerX} ${centerY}`,
        `L ${startX} ${startY}`,
        `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
        "Z",
    ].join(" ");
}

function getChartAmount(expense, month) {
    return Math.abs(expense.amountsByMonth.get(month.key) || 0);
}

function buildExpenseComparison(montlyReports, selectedYear) {
    const monthsByKey = new Map();
    const expensesById = new Map();
    const incomesById = new Map();

    montlyReports.filter((report) => Number(report.year) === selectedYear).forEach((report) => {
        const year = Number(report.year);
        const month = Number(report.month);
        const monthKey = `${year}-${String(month).padStart(2, "0")}`;

        monthsByKey.set(monthKey, {
            key: monthKey,
            label: monthKey,
            year,
            month,
        });

        report.expenses.forEach((expense) => {
            const amount = Number(expense.amount);
            const rowsById = amount > 0 ? incomesById : expensesById;

            if (amount === 0) {
                return;
            }

            if (!rowsById.has(expense.expenseId)) {
                rowsById.set(expense.expenseId, {
                    id: expense.expenseId,
                    title: expense.expenseTitle,
                    amountsByMonth: new Map(),
                });
            }

            const comparisonRow = rowsById.get(expense.expenseId);

            comparisonRow.amountsByMonth.set(
                monthKey,
                (comparisonRow.amountsByMonth.get(monthKey) || 0) + amount
            );
        });
    });

    return {
        expenses: Array.from(expensesById.values()).sort((left, right) =>
            left.title.localeCompare(right.title)
        ),
        incomes: Array.from(incomesById.values()).sort((left, right) =>
            left.title.localeCompare(right.title)
        ),
        months: Array.from(monthsByKey.values()).sort(
            (left, right) => left.year - right.year || left.month - right.month
        ),
    };
}

function buildComparisonYears(montlyReports, currentYear) {
    return Array.from(
        new Set([
            currentYear,
            ...montlyReports.map((report) => Number(report.year)),
        ])
    ).sort((left, right) => right - left);
}
